import FileUploadIcon from '@mui/icons-material/FileUpload';
import { Avatar, Box, CircularProgress, Typography, styled } from '@mui/material';
import React, { Fragment, useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useDispatch } from 'react-redux';

import { addToast } from '@sophocles/features/toast';

const StyledFileDropZone = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  height: 200,
  borderRadius: theme.shape.borderRadius,
  border: '1px solid rgba(0, 0, 0, 0.23)',
  backgroundColor: 'var(--surface-secondary)',
  padding: '16.5px 14px',
  '& .MuiAvatar-root': {
    width: 'auto',
    height: 'auto',
    position: 'relative',
    overflow: 'visible',
    backgroundColor: 'var(--interactive-hover)',
    transition: theme.transitions.create(['background-color', 'transform', 'margin'], {
      duration: theme.transitions.duration.standard,
    }),
  },
  '& .MuiSvgIcon-root': {
    fill: 'var(--text-secondary)',
    transition: theme.transitions.create(['fill', 'transform'], {
      duration: theme.transitions.duration.standard,
    }),
  },
  '& .MuiTypography-root': {
    color: 'var(--text-secondary)',
    transition: theme.transitions.create(['color'], {
      duration: theme.transitions.duration.shortest,
    }),
  },
  '&.dragging': {
    backgroundColor: theme.palette.primary.light,
    '.MuiSvgIcon-root': {
      fill: theme.palette.primary.dark,
    },
    '.MuiAvatar-root': {
      position: 'absolute',
      transform: 'scale(1.3)',
      backgroundColor: theme.palette.primary.light,
    },
  },
  '&:hover': {
    cursor: 'pointer',
    backgroundColor: 'var(--interactive-hover)',
    borderColor: 'rgba(0, 0, 0, 0.75)',
    '& .MuiAvatar-root': {
      transform: 'scale(1.1)',
      backgroundColor: theme.palette.primary.light,
      marginBottom: '16px',
    },
    '& .MuiSvgIcon-root': {
      fill: theme.palette.primary.dark,
    },
    '& .MuiTypography-root': {
      color: theme.palette.primary.dark,
    },
  },
}));

function FileDropZone({ children, onFilesAdded, config = {}, sx = [] }) {
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [isFilePickerOpen, setIsFilePickerOpen] = useState(false);

  // v14 accept is an object ({ 'mime/type': ['.ext'] }); legacy callers pass a CSV string.
  const acceptedFileFormats = (() => {
    if (!config.accept) return [];
    if (typeof config.accept === 'object') {
      return Object.values(config.accept)
        .flat()
        .map(ext => ext.replace(/^\./, '').toUpperCase());
    }
    return config.accept.split(',').map(a => {
      let format = a.trim();
      try {
        format = format.split('/')[1].toUpperCase();
      } catch (_) {
        format = format.toUpperCase();
      }
      return format;
    });
  })();

  function readFile(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onabort = () => {
        dispatch(
          addToast({
            title: 'Error',
            message: 'File reading was aborted, upload failed.',
            level: 'error',
            dismissible: true,
          }),
        );
        reject(new Error('File reading was aborted, upload failed.'));
      };

      reader.onerror = () => {
        dispatch(
          addToast({
            title: 'Error',
            message: 'Error processing file upload.',
            level: 'error',
            dismissible: true,
          }),
        );
        reject(new Error('Error processing file upload.'));
      };

      reader.onload = () => {
        const binaryStr = reader.result;
        resolve([file, binaryStr]);
      };

      reader.readAsArrayBuffer(file);
    });
  }

  const onDrop = acceptedFiles => {
    setLoading(true);

    const promises = []; // collect all promises
    acceptedFiles.forEach(file => {
      promises.push(readFile(file));
    });

    Promise.all(promises) // wait for the resolutions
      .then(results => {
        onFilesAdded(results);
        //results.forEach(result => {
        //  onFileAdded(result[0], result[1]);
        //});
      });

    setIsFilePickerOpen(false);

    setLoading(false);
  };

  const onDropRejected = useCallback(rejectedFiles => {
    dispatch(
      addToast({
        title: 'Error',
        message: `The selected files were not the right type! Must be one of the following: ${acceptedFileFormats.join(
          ', ',
        )}`,
        level: 'error',
        dismissible: true,
      }),
    );
    setIsFilePickerOpen(false);
    setIsHovering(false);
    setLoading(false);
  });

  const onFileDialogOpen = useCallback(() => {
    setIsFilePickerOpen(true);
  }, []);

  const onFileDialogCancel = useCallback(() => {
    setIsFilePickerOpen(false);
    setIsHovering(false);
  }, []);

  const {
    getRootProps,
    getInputProps,
    isFocused,
    isDragActive,
    isDragAccept,
    isDragReject,
    acceptedFiles,
    fileRejections,
  } = useDropzone({
    onDrop,
    onFileDialogOpen,
    onFileDialogCancel,
    onDropRejected,
    ...config,
  });

  const isBusy = isDragActive || isFilePickerOpen;

  return (
    <StyledFileDropZone
      className={'odl-ripple' + (isBusy ? ' dragging' : '')}
      {...getRootProps()}
      sx={[...(Array.isArray(sx) ? sx : [sx])]}
      onMouseEnter={e => {
        setIsHovering(true);
      }}
      onMouseLeave={e => {
        if (!isBusy) {
          setIsHovering(false);
        }
      }}
    >
      <input {...getInputProps()} />

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          height: '100%',
          '>:not(.MuiTypography-root)': { mb: 1 },
        }}
      >
        {loading ? (
          <Fragment>
            <CircularProgress sx={{ mb: 2 }} />
            <Typography variant="body1">Processing...</Typography>
          </Fragment>
        ) : (
          <Fragment>
            <Avatar className={isHovering || isBusy ? 'odl-ripple-pulse' : ''}>
              <FileUploadIcon sx={{ fontSize: '5em' }} />
            </Avatar>
            {children({ isBusy })}
            {!isBusy && (
              <Fragment>
                {acceptedFileFormats.length > 0 && (
                  <Typography variant="caption" sx={theme => ({ color: theme.palette.text.secondary })}>
                    Accepted formats: {acceptedFileFormats.join(', ')}
                  </Typography>
                )}
                {config.maxFiles && (
                  <Typography variant="caption" sx={theme => ({ color: theme.palette.text.secondary })}>
                    Maximum of {config.maxFiles} files
                  </Typography>
                )}
              </Fragment>
            )}
          </Fragment>
        )}
      </Box>
    </StyledFileDropZone>
  );
}

export default FileDropZone;
